import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { SampleDataStatusResponse } from "./contracts/SampleDataStatusResponse";
import {
  fetchSampleDataStatus,
  loadSampleData,
  removeSampleData,
} from "./slice-extensions/sample-data-slice-extension";

interface SampleDataState {
  status: SampleDataStatusResponse | null;
  fetchStatus: LoadingStatus;
  /** Load / remove in flight — both mutate the whole set, never in parallel. */
  mutationStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: SampleDataState = {
  status: null,
  fetchStatus: LoadingStatus.Idle,
  mutationStatus: LoadingStatus.Idle,
  lastError: undefined,
};

const inactiveStatus: SampleDataStatusResponse = {
  active: false,
  createdAt: null,
  languageCode: null,
  counts: null,
  memberIds: [],
  groupIds: [],
  sessionIds: [],
  highlights: null,
  issues: [],
};

const sampleDataSlice = createSlice({
  name: "sampleData",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSampleDataStatus.pending, (state) => {
        state.fetchStatus = LoadingStatus.Loading;
      })
      .addCase(
        fetchSampleDataStatus.fulfilled,
        (state, action: PayloadAction<SampleDataStatusResponse>) => {
          state.fetchStatus = LoadingStatus.Idle;
          state.status = action.payload;
        },
      )
      .addCase(fetchSampleDataStatus.rejected, (state, action) => {
        state.fetchStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(loadSampleData.pending, (state) => {
        state.mutationStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(
        loadSampleData.fulfilled,
        (state, action: PayloadAction<SampleDataStatusResponse>) => {
          state.mutationStatus = LoadingStatus.Idle;
          state.status = action.payload;
        },
      )
      .addCase(loadSampleData.rejected, (state, action) => {
        state.mutationStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(removeSampleData.pending, (state) => {
        state.mutationStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(removeSampleData.fulfilled, (state) => {
        state.mutationStatus = LoadingStatus.Idle;
        state.status = inactiveStatus;
      })
      .addCase(removeSampleData.rejected, (state, action) => {
        state.mutationStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });
  },
});

export const selectSampleDataStatus = (state: RootState) => state.sampleData.status;
export const selectSampleDataActive = (state: RootState) =>
  state.sampleData.status?.active ?? false;
export const selectSampleDataFetchStatus = (state: RootState) => state.sampleData.fetchStatus;
export const selectSampleDataMutationStatus = (state: RootState) =>
  state.sampleData.mutationStatus;
export const selectSampleDataError = (state: RootState) => state.sampleData.lastError;
export const selectSampleMemberIds = (state: RootState) =>
  state.sampleData.status?.memberIds ?? [];
export const selectSampleGroupIds = (state: RootState) => state.sampleData.status?.groupIds ?? [];
export const selectSampleSessionIds = (state: RootState) =>
  state.sampleData.status?.sessionIds ?? [];

export default sampleDataSlice.reducer;
